// ===========================================
// Onboarding Settings Tab Component
// ===========================================
// Admin UI for configuring the student onboarding flow
// 8 sections with real-time toggle conflict validation

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import {
  useOnboardingConfig,
  useUpdateOnboardingConfig,
  useResetOnboardingConfig,
} from '../../../hooks/useAdminSettings';
import type {
  OnboardingConfigInput,
  CustomRegistrationField,
  ToggleValidation,
} from '../../../api/adminSettings.api';
import CustomFieldsBuilder from './CustomFieldsBuilder';
import { usePermissions } from '../../../hooks/usePermissions';
import { useOwnerTitle } from '../../../hooks/useOwnerTitle';
import SettingsWriteGate from './SettingsWriteGate';
import RegistrationFeeCreditCard from './RegistrationFeeCreditCard';

// ===========================================
// TYPES
// ===========================================

interface FormData {
  requireMeetAndGreet: boolean;
  requireRegistrationForMeetAndGreet: boolean;
  requireAdminApproval: boolean;
  autoApproveStudents: boolean;
  allowClassSelectionAtRegistration: boolean;
  waitlistBehavior: 'ADD_TO_WAITLIST' | 'SHOW_ALTERNATIVES' | 'NOTIFY_ADMIN';
  includeFreeTrialClass: boolean;
  freeTrialClassCount: number;
  freeTrialScope: 'PER_STUDENT' | 'PER_FAMILY';
  requirePayment: boolean;
  registrationFee: number | null;
  requireTermsAcceptance: boolean;
  termsAndConditionsUrl: string;
  privacyPolicyUrl: string;
  requirePhotoConsent: boolean;
  photoConsentDescription: string;
  onboardingExpiryDays: number;
  allowMultiStudentRegistration: boolean;
}

// ===========================================
// TOGGLE VALIDATION (client-side mirror of backend)
// ===========================================

function validateToggles(config: FormData): ToggleValidation[] {
  const validations: ToggleValidation[] = [];

  if (
    !config.requireMeetAndGreet &&
    !config.allowClassSelectionAtRegistration &&
    !config.requireAdminApproval
  ) {
    validations.push({
      type: 'error',
      message:
        'Students will register but never be assigned a class. Enable either Class Selection at Registration or Admin Approval.',
      affectedToggles: [
        'requireMeetAndGreet',
        'allowClassSelectionAtRegistration',
        'requireAdminApproval',
      ],
    });
  }

  if (!config.requirePayment && config.includeFreeTrialClass) {
    validations.push({
      type: 'warning',
      message:
        'Free trial is redundant when payment is disabled — trial classes will function as regular classes.',
      affectedToggles: ['requirePayment', 'includeFreeTrialClass'],
    });
  }

  if (config.requireMeetAndGreet && config.allowClassSelectionAtRegistration) {
    validations.push({
      type: 'warning',
      message:
        'Class selection at registration is ignored when Meet & Greet is enabled — class assignment happens during the Meet & Greet.',
      affectedToggles: ['requireMeetAndGreet', 'allowClassSelectionAtRegistration'],
    });
  }

  if (config.requireAdminApproval && config.autoApproveStudents) {
    validations.push({
      type: 'warning',
      message:
        'Auto-approve is enabled — this effectively skips the approval step. Consider turning off Require Admin Approval instead.',
      affectedToggles: ['requireAdminApproval', 'autoApproveStudents'],
    });
  }

  if (!config.requireTermsAcceptance) {
    validations.push({
      type: 'warning',
      message: 'We recommend requiring Terms & Conditions acceptance for legal protection.',
      affectedToggles: ['requireTermsAcceptance'],
    });
  }

  return validations;
}

// ===========================================
// FLOW PREVIEW
// ===========================================

function buildStepLabels(config: FormData): string[] {
  const steps: string[] = [];
  const mgWithoutReg = config.requireMeetAndGreet && !config.requireRegistrationForMeetAndGreet;

  // When M&G is on but registration isn't required for M&G,
  // the flow starts with M&G (separate booking page), then registration after approval
  if (mgWithoutReg) {
    steps.push('Meet & Greet (public booking)');
    steps.push('Admin Approval');
    steps.push('Registration');
  } else if (config.requireMeetAndGreet && config.requireRegistrationForMeetAndGreet) {
    steps.push('Registration');
  } else {
    steps.push('Registration');
  }

  if (config.requireTermsAcceptance) {
    steps.push('Terms & Conditions');
  }

  if (config.allowClassSelectionAtRegistration && !mgWithoutReg) {
    steps.push('Class Selection');
  }

  // M&G scheduling only when registration is required before M&G
  if (config.requireMeetAndGreet && config.requireRegistrationForMeetAndGreet) {
    steps.push('Meet & Greet');
  }

  // Admin approval (only shown when not already shown for mgWithoutReg flow above)
  if (config.requireAdminApproval && !config.autoApproveStudents && !mgWithoutReg) {
    steps.push('Admin Approval');
  }

  if (config.includeFreeTrialClass && !config.requireMeetAndGreet) {
    steps.push('Free Trial');
  }

  if (config.requirePayment && !config.includeFreeTrialClass) {
    steps.push('Payment');
  } else if (config.requirePayment && config.includeFreeTrialClass) {
    steps.push('Payment (after trial)');
  }

  steps.push('Active Student');

  return steps;
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function OnboardingSettingsTab() {
  // The registration fee and "Require Payment" are pricing: the server refuses a change to
  // either without operations_pricing.write (roles plan §5.7 review M2).
  const { can } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const canEditRegistrationPricing = can('operations_pricing', 'write');
  const { data: configResponse, isLoading, error } = useOnboardingConfig();
  const updateMutation = useUpdateOnboardingConfig();
  const resetMutation = useResetOnboardingConfig();
  // Reset turns "Require Payment" back on, which the server refuses without pricing write
  // when the stored value is off (it would be a pricing change).
  const resetBlockedByPricing =
    !canEditRegistrationPricing && configResponse?.data?.requirePayment === false;
  const [customFields, setCustomFields] = useState<CustomRegistrationField[]>([]);
  const [customFieldsDirty, setCustomFieldsDirty] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<FormData>({
    defaultValues: {
      requireMeetAndGreet: true,
      requireRegistrationForMeetAndGreet: false,
      requireAdminApproval: true,
      autoApproveStudents: false,
      allowClassSelectionAtRegistration: false,
      waitlistBehavior: 'ADD_TO_WAITLIST',
      includeFreeTrialClass: false,
      freeTrialClassCount: 1,
      freeTrialScope: 'PER_FAMILY',
      requirePayment: true,
      registrationFee: 50,
      requireTermsAcceptance: true,
      termsAndConditionsUrl: '',
      privacyPolicyUrl: '',
      requirePhotoConsent: false,
      photoConsentDescription: '',
      onboardingExpiryDays: 30,
      allowMultiStudentRegistration: true,
    },
  });

  const watchedValues = watch();

  // Populate form when data loads
  useEffect(() => {
    if (configResponse?.data) {
      const config = configResponse.data;
      reset({
        requireMeetAndGreet: config.requireMeetAndGreet,
        requireRegistrationForMeetAndGreet: config.requireRegistrationForMeetAndGreet,
        requireAdminApproval: config.requireAdminApproval,
        autoApproveStudents: config.autoApproveStudents,
        allowClassSelectionAtRegistration: config.allowClassSelectionAtRegistration,
        waitlistBehavior: config.waitlistBehavior,
        includeFreeTrialClass: config.includeFreeTrialClass,
        freeTrialClassCount: config.freeTrialClassCount,
        freeTrialScope: config.freeTrialScope,
        requirePayment: config.requirePayment,
        registrationFee: config.registrationFee ?? 50,
        requireTermsAcceptance: config.requireTermsAcceptance,
        termsAndConditionsUrl: config.termsAndConditionsUrl || '',
        privacyPolicyUrl: config.privacyPolicyUrl || '',
        requirePhotoConsent: config.requirePhotoConsent,
        photoConsentDescription: config.photoConsentDescription || '',
        onboardingExpiryDays: config.onboardingExpiryDays,
        allowMultiStudentRegistration: config.allowMultiStudentRegistration,
      });
      setCustomFields(config.customFields || []);
      setCustomFieldsDirty(false);
    }
  }, [configResponse, reset]);

  // Real-time toggle validation
  const validations = useMemo(() => validateToggles(watchedValues), [watchedValues]);
  const hasErrors = validations.some((v) => v.type === 'error');

  // Flow preview steps
  const flowSteps = useMemo(() => buildStepLabels(watchedValues), [watchedValues]);

  const onSubmit = (data: FormData) => {
    // Without pricing write, leave the two pricing fields out so the server keeps what is stored
    // (the form shows $50 for an unset fee, which would otherwise be saved as a change).
    const { registrationFee, requirePayment, ...rest } = data;
    const payload: OnboardingConfigInput = {
      ...rest,
      ...(canEditRegistrationPricing ? { registrationFee, requirePayment } : {}),
      termsAndConditionsUrl: data.termsAndConditionsUrl || null,
      privacyPolicyUrl: data.privacyPolicyUrl || null,
      photoConsentDescription: data.photoConsentDescription || null,
      customFields,
    };
    updateMutation.mutate(payload);
    setCustomFieldsDirty(false);
  };

  const handleCustomFieldsChange = (fields: CustomRegistrationField[]) => {
    setCustomFields(fields);
    setCustomFieldsDirty(true);
  };

  const handleReset = () => {
    if (window.confirm('Reset onboarding configuration to defaults? This cannot be undone.')) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Skeleton variant="rectangular" height={100} />
        <Skeleton variant="rectangular" height={200} />
        <Skeleton variant="rectangular" height={200} />
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load onboarding settings: {(error as Error).message}</Alert>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {/* Flow Preview */}
        <Card variant="outlined">
          <CardHeader
            title="Onboarding Flow Preview"
            subheader="This is the path new students will follow based on your current settings"
          />
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              {flowSteps.map((step, index) => (
                <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={step}
                    color={index === 0 ? 'primary' : index === flowSteps.length - 1 ? 'success' : 'default'}
                    variant={index === 0 || index === flowSteps.length - 1 ? 'filled' : 'outlined'}
                  />
                  {index < flowSteps.length - 1 && (
                    <ArrowIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Validation Warnings */}
        {validations.length > 0 && (
          <Stack spacing={1}>
            {validations.map((v, i) => (
              <Alert key={i} severity={v.type === 'error' ? 'error' : 'warning'}>
                {v.message}
              </Alert>
            ))}
          </Stack>
        )}

        {/* Section 1: Meet & Greet & Approval */}
        <Card variant="outlined">
          <CardHeader
            title="Meet & Greet & Approval"
            subheader="Control whether new students need a meet & greet session and/or admin approval"
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="requireMeetAndGreet"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label={
                      <Box>
                        <Typography variant="body1">Require Meet & Greet</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Students must complete a meet & greet session before being added to classes
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />

              {watchedValues.requireMeetAndGreet && (
                <Box sx={{ pl: 4 }}>
                  <Controller
                    name="requireRegistrationForMeetAndGreet"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={field.value} onChange={field.onChange} />}
                        label={
                          <Box>
                            <Typography variant="body1">Require Registration Before Meet & Greet</Typography>
                            <Typography variant="body2" color="text.secondary">
                              If off, parents book a Meet & Greet without registering first. Registration happens after approval. This is the Music 'n Me default.
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />
                </Box>
              )}

              <Controller
                name="requireAdminApproval"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label={
                      <Box>
                        <Typography variant="body1">Require Admin Approval</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Admin must approve student registration before they can access classes
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />

              {watchedValues.requireAdminApproval && (
                <Box sx={{ pl: 4 }}>
                  <Controller
                    name="autoApproveStudents"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={field.value} onChange={field.onChange} />}
                        label={
                          <Box>
                            <Typography variant="body1">Auto-approve Students</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Automatically approve without admin intervention
                            </Typography>
                          </Box>
                        }
                      />
                    )}
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Section 2: Class Enrolment */}
        <Card variant="outlined">
          <CardHeader
            title="Class Enrolment"
            subheader="Configure how students are assigned to classes during onboarding"
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="allowClassSelectionAtRegistration"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label={
                      <Box>
                        <Typography variant="body1">Allow Class Selection at Registration</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Parents can browse and select a class during registration
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />

              {watchedValues.allowClassSelectionAtRegistration && (
                <Box sx={{ pl: 4 }}>
                  <Controller
                    name="waitlistBehavior"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth size="small">
                        <InputLabel>When Class is Full</InputLabel>
                        <Select {...field} label="When Class is Full">
                          <MenuItem value="ADD_TO_WAITLIST">Add to Waitlist</MenuItem>
                          <MenuItem value="SHOW_ALTERNATIVES">Show Alternative Classes</MenuItem>
                          <MenuItem value="NOTIFY_ADMIN">Notify Admin</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              )}

              <Controller
                name="includeFreeTrialClass"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label={
                      <Box>
                        <Typography variant="body1">Include Free Trial Class</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Students receive free trial classes before payment is required
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />

              {watchedValues.includeFreeTrialClass && (
                <Box sx={{ pl: 4, display: 'flex', gap: 2 }}>
                  <Controller
                    name="freeTrialClassCount"
                    control={control}
                    render={({ field }) => (
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Trial Classes</InputLabel>
                        <Select {...field} label="Trial Classes">
                          <MenuItem value={1}>1 class</MenuItem>
                          <MenuItem value={2}>2 classes</MenuItem>
                          <MenuItem value={3}>3 classes</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />

                  <Controller
                    name="freeTrialScope"
                    control={control}
                    render={({ field }) => (
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Trial Scope</InputLabel>
                        <Select {...field} label="Trial Scope">
                          <MenuItem value="PER_STUDENT">Per Student</MenuItem>
                          <MenuItem value="PER_FAMILY">Per Family</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Section 3: Payment */}
        <Card variant="outlined">
          <CardHeader
            title="Payment"
            subheader="Configure payment requirements during onboarding"
          />
          <CardContent>
            <Controller
              name="requirePayment"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={!canEditRegistrationPricing}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">Require Payment</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Payment is collected during or after onboarding
                        {watchedValues.includeFreeTrialClass
                          ? ' (after trial classes complete)'
                          : ''}
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
            {watchedValues.requirePayment && (
              <Box sx={{ mt: 2, ml: 6 }}>
                <Controller
                  name="registrationFee"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Registration Fee ($)"
                      type="number"
                      size="small"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === '' ? null : Number(val));
                      }}
                      inputProps={{ min: 0, max: 10000, step: 0.01 }}
                      sx={{ width: 200 }}
                      disabled={!canEditRegistrationPricing}
                      helperText={
                        canEditRegistrationPricing
                          ? 'Amount charged for student registration'
                          : 'Changing the fee needs the Pricing (edit) permission'
                      }
                    />
                  )}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* #374: crediting a paid registration fee on the child's first invoice (owner-only to change) */}
        <RegistrationFeeCreditCard />

        {/* Section 4: Legal & Compliance */}
        <Card variant="outlined">
          <CardHeader
            title="Legal & Compliance"
            subheader="Terms acceptance and photo consent requirements"
          />
          <CardContent>
            <Stack spacing={2}>
              <Controller
                name="requireTermsAcceptance"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={field.onChange} />}
                    label={
                      <Box>
                        <Typography variant="body1">Require Terms & Conditions Acceptance</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Parents must accept terms and privacy policy before completing onboarding
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />

              {watchedValues.requireTermsAcceptance && (
                <Box sx={{ pl: 4 }}>
                  <Stack spacing={2}>
                    <Alert severity="info" sx={{ alignItems: 'center' }}>
                      <Typography variant="body2">
                        Configure your Terms & Conditions and Privacy Policy content in the{' '}
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight={600}
                          color="primary"
                          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => {
                            // Navigate to Legal tab (index 7) in parent settings page
                            const tabsEl = document.querySelector('[role="tablist"]');
                            const legalTab = tabsEl?.querySelectorAll('[role="tab"]')[7] as HTMLElement;
                            legalTab?.click();
                          }}
                        >
                          Legal tab
                        </Typography>
                        .
                      </Typography>
                    </Alert>

                    <Controller
                      name="requirePhotoConsent"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Switch checked={field.value} onChange={field.onChange} />}
                          label={
                            <Box>
                              <Typography variant="body1">Require Photo Consent for Marketing</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Parents must explicitly consent to photo/video use for marketing
                              </Typography>
                            </Box>
                          }
                        />
                      )}
                    />

                    {watchedValues.requirePhotoConsent && (
                      <Controller
                        name="photoConsentDescription"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Photo Consent Description"
                            placeholder="I consent to photos/videos being used on social media and marketing materials"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                          />
                        )}
                      />
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Section 5: Cleanup & Data Management */}
        <Card variant="outlined">
          <CardHeader
            title="Cleanup & Data Management"
            subheader="Configure how incomplete registrations are handled"
          />
          <CardContent>
            <Controller
              name="onboardingExpiryDays"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 30)}
                  type="number"
                  label="Onboarding Expiry (Days)"
                  helperText="Incomplete registrations are marked as abandoned after this many days of inactivity"
                  size="small"
                  sx={{ width: 250 }}
                  inputProps={{ min: 7, max: 365 }}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Section 6: Multi-Student Registration */}
        <Card variant="outlined">
          <CardHeader
            title="Multi-Student Registration"
            subheader="Allow parents to enrol multiple children in one session"
          />
          <CardContent>
            <Controller
              name="allowMultiStudentRegistration"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={
                    <Box>
                      <Typography variant="body1">Allow Multi-Student Registration</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Parents can add multiple children during a single registration session
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </CardContent>
        </Card>

        {/* Section 7: Custom Registration Fields */}
        <Card variant="outlined">
          <CardHeader
            title="Custom Registration Fields"
            subheader="Add school-specific questions to the registration form (up to 10)"
          />
          <CardContent>
            <CustomFieldsBuilder
              fields={customFields}
              onChange={handleCustomFieldsChange}
              maxFields={10}
            />
          </CardContent>
        </Card>

        {/* Section 8: Age / Student Category */}
        <Card variant="outlined">
          <CardHeader
            title="Age / Student Category"
            subheader="How the system handles different student ages"
          />
          <CardContent>
            <Alert severity="info" icon={<InfoIcon />}>
              The system automatically detects whether a student is a child (under 18) or an adult
              based on their date of birth. Child registrations require parent/guardian details and
              consent. Adult students can self-register without a parent section.
            </Alert>
          </CardContent>
        </Card>

        {/* Save / Reset Buttons */}
        <SettingsWriteGate>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                resetBlockedByPricing
                  ? `Reset would turn "Require Payment" back on, which needs Class Pricing edit access. Ask your ${ownerTitle} to reset.`
                  : ''
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleReset}
                  disabled={resetMutation.isPending || resetBlockedByPricing}
                >
                  Reset to Defaults
                </Button>
              </span>
            </Tooltip>
            <Button
              type="submit"
              variant="contained"
              disabled={(!isDirty && !customFieldsDirty) || hasErrors || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </SettingsWriteGate>
      </Stack>
    </Box>
  );
}
